import ol, { Extent } from 'openlayers'
import { Dispatch } from 'react'
import { mapActions } from 'src/actions/MapActions'
import { wikiActions } from 'src/actions/WikiActions'
import { HydroModelObject } from 'src/dataSources/geo/HydroModel'
import { getLayerDescriptionForType, LayerCapabilities, LayerType } from 'src/dataSources/layers/LayerRegistry'
import { getLayerCapabilitiesFromType, getLayerParamsFromType, MapState, mapStateInitial } from 'src/reducers/MapReducer'

/**
 * Gets the capabilities for a layer from the capabilitiesUrl.
 * @param layer the layer for witch the capabilities are needed
 * @param layerCapabilities a map of the current capabilities from state
 * @returns the capabilities object for the layer
 */
export const getLayerCapabilities = async (
  layer: LayerType,
  capabilitiesUrl: string,
  layerCapabilities: LayerCapabilities[]
): Promise<ol.GlobalObject|undefined> => {
  let capabilities = getLayerCapabilitiesFromType(layer, layerCapabilities)
  try {
    const response = await fetch(capabilitiesUrl)
    const fetchedCapabilities = await response.text()

    const parser = new ol.format.WMTSCapabilities()
    capabilities = parser.read(fetchedCapabilities)
  } catch (error) {
    console.error(error)
  }

  return capabilities
}

/**
 * Resets the selected layers to the default selection
 * @param enabledLayers The currently enabled layers
 * @param dispatch Dispatch object to dispatch Redux state change actions
 */
export const resetSelectedLayers = (enabledLayers: LayerType[], dispatch: Dispatch<any>): void => {
  const defaultLayers = mapStateInitial.enabledLayers
  enabledLayers.forEach(layerType => {
    if (!defaultLayers.includes(layerType)) {
      const contextTags = getLayerDescriptionForType(layerType).contextTags
      dispatch(wikiActions.removeContextTags(contextTags))
    }
  })
  const tagsToAdd = defaultLayers.map(layer => getLayerDescriptionForType(layer).contextTags).flat()
  dispatch(wikiActions.addContextTags(tagsToAdd))
  dispatch(mapActions.clearLayers())
}

/**
 * Get comma separated string of selected hydro model ids.
 * @param mapState Map state to search through.
 * @returns Comma separated string with ids.
 */
export const getSelectedHydroModelLayers = (mapState: MapState): string => {
  const params = getLayerParamsFromType(LayerType.HYDRO_MODEL, mapState.layerParams)
  let hydroModelIds = ''
  if (params?.switchSubLayers !== undefined) {
    for (const layer of params.switchSubLayers?.sublayers) {
      if (!layer.enabled) {
        continue
      }
      if (layer.id === undefined) {
        continue
      }
      hydroModelIds = [hydroModelIds, layer.id].filter(Boolean).join(',')
    }
  }
  return hydroModelIds
}

/**
 * Gets sub layers from capabilities.
 * Retrieves the available sub layers from capabilities
 * @param capabilities the capabilities from witch to retrieve the available sub layers
 * @returns the a list of sub layers
 */
export const getSubLayersFromCapabilities = (
  capabilities: ol.GlobalObject
): string[] => {
  let layers = []
  if (capabilities.Contents !== undefined) {
    layers = capabilities.Contents.Layer.map((layer: { Identifier: any }) => {
      return layer.Identifier
    })
  }

  return layers
}

/**
 * Determine if a given point intersects a given map extent.
 * @param mapExtent Extent object.
 * @param x x-value of point
 * @param y y-value of point
 * @returns true inf intersecting, false otherwise.
 */
export const isPointIntersectingMapExtent = (mapExtent: Extent, x: number, y: number): boolean => {
  return x > mapExtent[0] && x < mapExtent[2] && y > mapExtent[1] && y < mapExtent[3]
}

/**
 * Determine if one of the corners of b2 is within the bounds of b1.
 * @param b1 Extent object.
 * @param b2 Extent object.
 * @returns true if intersecting, false otherwise.
 */
const areBoxesIntersecting = (b1: Extent, b2: Extent): boolean => {
  // Top left (b1)
  if (isPointIntersectingMapExtent(b1, b2[0], b2[3])) {
    return true
  }

  // Top right (b1)
  if (isPointIntersectingMapExtent(b1, b2[2], b2[3])) {
    return true
  }

  // Bottom left (b1)
  if (isPointIntersectingMapExtent(b1, b2[0], b2[1])) {
    return true
  }

  // Bottom right (b1)
  if (isPointIntersectingMapExtent(b1, b2[2], b2[1])) {
    return true
  }
  return false
}

/**
 * Determine if a given hydro model intersects a given map extent.
 * @param mapExtent Extent object.
 * @param hydroModel HydroModel object.
 * @returns true if intersecting, false otherwise.
 */
export const isHydroModelIntersectingMapExtent = (mapExtent: Extent, hydroModel: HydroModelObject): boolean => {
  const hydroExtend: Extent = [
    hydroModel.areaInfo.ModelBbox.MinX,
    hydroModel.areaInfo.ModelBbox.MinY,
    hydroModel.areaInfo.ModelBbox.MaxX,
    hydroModel.areaInfo.ModelBbox.MaxY
  ]
  return areBoxesIntersecting(mapExtent, hydroExtend) || areBoxesIntersecting(hydroExtend, mapExtent)
}
